import React, { useState } from 'react';
import { generateResponseStyle } from '../../services/factFinderApi';

const STYLES = [
  { key: 'brief_and_cautious', label: 'Brief & Cautious', desc: 'Minimal answers, stay guarded' },
  { key: 'factual_but_firm', label: 'Factual but Firm', desc: 'Direct and clear, push back politely' },
  { key: 'steward_ready', label: 'Steward-Ready', desc: 'Assertive with union rep present' },
  { key: 'employee_only_prep', label: 'Employee-Only Prep', desc: 'Extra cautious, no rep present' },
];

function ResponseStylePanel({ caseData }) {
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleGenerate(styleKey) {
    setSelectedStyle(styleKey);
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await generateResponseStyle(caseData, styleKey);
      if (res.success) {
        setResult(res);
      } else {
        setError(res.error || 'Style generation failed.');
      }
    } catch (err) {
      setError(err.message || 'Style generation failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="ff-card ff-p4-panel" data-testid="response-style-panel">
      <h3>Response Style Generator</h3>
      <p className="ff-card-description">
        Choose your approach and get tailored response guidance.
      </p>

      <div className="ff-style-buttons" data-testid="style-buttons">
        {STYLES.map((s) => (
          <button
            key={s.key}
            className={`ff-style-btn ${selectedStyle === s.key ? 'ff-style-active' : ''}`}
            onClick={() => handleGenerate(s.key)}
            disabled={loading}
            data-testid={`style-btn-${s.key}`}
          >
            <span className="ff-style-btn-label">{s.label}</span>
            <span className="ff-style-btn-desc">{s.desc}</span>
          </button>
        ))}
      </div>

      {loading && (
        <div className="ff-analyzing-indicator">Generating response style...</div>
      )}

      {error && <div className="ff-inline-error">{error}</div>}

      {result && !loading && (
        <div className="ff-p4-result" data-testid="response-style-result">
          {/* Style Header */}
          <div className="ff-style-header">
            <h4>{result.style_name}</h4>
            {result.style_description && (
              <p className="ff-style-desc-text">{result.style_description}</p>
            )}
          </div>

          {/* Opening Statement */}
          {result.opening_statement && (
            <div className="ff-prep-section ff-prep-highlight">
              <h4>Opening Statement</h4>
              <p>{result.opening_statement}</p>
            </div>
          )}

          {/* Tone Guidance */}
          {result.tone_guidance && (
            <div className="ff-prep-section">
              <h4>Tone & Demeanor</h4>
              <p>{result.tone_guidance}</p>
            </div>
          )}

          {/* Sample Responses */}
          {result.sample_responses?.length > 0 && (
            <div className="ff-prep-section">
              <h4>Sample Responses</h4>
              <div className="ff-sample-list">
                {result.sample_responses.map((sr, idx) => (
                  <div key={idx} className="ff-sample-item" data-testid={`sample-${idx}`}>
                    <div className="ff-sample-scenario">{sr.scenario}</div>
                    <div className="ff-sample-response">{sr.response}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key Phrases */}
          {result.key_phrases?.length > 0 && (
            <div className="ff-prep-section">
              <h4>Key Phrases to Use</h4>
              <div className="ff-phrases-chips">
                {result.key_phrases.map((phrase, idx) => (
                  <span key={idx} className="ff-phrase-chip ff-chip-good">{phrase}</span>
                ))}
              </div>
            </div>
          )}

          {/* Things to Avoid */}
          {result.things_to_avoid?.length > 0 && (
            <div className="ff-prep-section ff-prep-warning">
              <h4>Things to Avoid</h4>
              <ul>
                {result.things_to_avoid.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ResponseStylePanel;
